// Tous les emplacements de fichiers possibles sur un lead (FormLEAD), quelle
// que soit l'étape du pipeline (formulaire de dépôt, formulaire de
// préparation de RDV rdv_document_1..5). Un lead n'en a en pratique presque
// jamais la totalité : on ne montre que ceux réellement renseignés.
export const LEAD_DOCUMENT_FIELDS = [
  { key: "pieceIdentite", label: "Pièce d'identité" },
  { key: "bilanDocument", label: "Dernier bilan disponible" },
  { key: "identityDocument", label: "Pièce d'identité du gérant" },
  { key: "kbisDocument", label: "KBIS" },
  { key: "statusDocument", label: "Statuts" },
  { key: "rdv_document_1", label: "Document RDV 1" },
  { key: "rdv_document_2", label: "Document RDV 2" },
  { key: "rdv_document_3", label: "Document RDV 3" },
  { key: "rdv_document_4", label: "Document RDV 4" },
  { key: "rdv_document_5", label: "Document RDV 5" },
];

/**
 * Les fichiers déposés via le formulaire de préparation de RDV
 * (alfacomptabilite/rendez-vous/[id]) sont libres — contrairement aux 5 autres
 * slots (KBIS, statuts...) qui ont un sens fixe — donc on affiche le nom du
 * fichier réellement envoyé plutôt qu'un intitulé générique "Document RDV n".
 */
function isRdvDocument(key) {
  return key.startsWith("rdv_document_");
}

/**
 * Certains noms de fichiers accentués ressortent double-encodés côté upload
 * (ex. "résultat" -> "rÃ©sultat"). On tente de réinterpréter les caractères
 * concernés (code 0x80-0xff) comme des octets UTF-8 ; `fatal: true` fait
 * échouer la tentative (et on garde le nom tel quel) si le texte n'était en
 * réalité pas concerné.
 */
function fixDoubleUtf8Encoding(str) {
  const hasHighByteChar = Array.from(str).some((char) => {
    const code = char.charCodeAt(0);
    return code >= 128 && code <= 255;
  });

  if (!hasHighByteChar) return str;

  try {
    const bytes = Uint8Array.from(Array.from(str).map((c) => c.charCodeAt(0)));
    return new TextDecoder("utf-8", { fatal: true }).decode(bytes);
  } catch {
    return str;
  }
}

/**
 * Le nom d'origine est encodé dans l'URL Vercel Blob par
 * `formLEADController.js` : `uploads/<timestamp>_<nom-encodé>-<suffixe>.<ext>`.
 * On le reconstruit ; en cas d'échec (format inattendu), on retombe sur
 * l'intitulé générique du slot.
 */
function displayDocumentName(url, fallbackLabel) {
  if (!url) return fallbackLabel;

  try {
    const last = url.split("/").pop();
    let name = decodeURIComponent(last);

    name = fixDoubleUtf8Encoding(name);
    name = name.replace(/^\d+_/, ""); // horodatage d'upload
    name = name.replace(/-[A-Za-z0-9]{15,}(\.[^.]+)$/, "$1"); // suffixe aléatoire Vercel Blob
    name = name.replace(/\s+(\.[^.\s]+)$/, "$1"); // espace résiduel avant l'extension

    return name || fallbackLabel;
  } catch {
    return fallbackLabel;
  }
}

/**
 * Liste des documents réellement partagés par un lead/prospect.
 * N'affiche que les champs renseignés (pas de slots vides "Aucun fichier") ;
 * si aucun document n'est présent, affiche un message d'explication.
 */
export default function LeadDocumentsList({ lead }) {
  const documents = LEAD_DOCUMENT_FIELDS.filter((doc) => lead?.[doc.key]);

  if (documents.length === 0) {
    return (
      <p className="text-xs text-slate-500 italic">
        Le prospect n'a partagé aucun document. Si besoin, vous pouvez
        l'importer manuellement en modifiant cette fiche prospect.
      </p>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-y-3 gap-x-10 text-xs">
      {documents.map((doc) => {
        const url = lead[doc.key];
        const label = isRdvDocument(doc.key)
          ? displayDocumentName(url, doc.label)
          : doc.label;

        return (
          <div
            key={doc.key}
            className="flex justify-between items-center gap-3"
          >
            <span className="text-slate-500 truncate" title={label}>
              {label}
            </span>
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#9B1B5D] font-medium hover:underline shrink-0"
            >
              Voir le document
            </a>
          </div>
        );
      })}
    </div>
  );
}
